import JwtService from "@/core/services/JwtService";
import ApiService from "@/core/services/ApiService";
import { useGlobalStore } from "@/pinia/global";
import {webPushIdentityChanging} from "@/services/webPush.service";

/**
 * Super-admin "log in as partner" (impersonation).
 *
 * The backend (POST /login-as, super-admin gated) mints a short-lived token for
 * one of the partner company's administrators and returns the same payload as a
 * normal login. Here we back up the super-admin's own token, swap in the partner
 * token, and hard-reload so every store re-hydrates as that partner. "Back"
 * restores the original token. State survives reloads via localStorage; the
 * presence of the backup key is the single source of truth for "am I impersonating".
 */
const IMP_TOKEN_KEY = "impersonator_token";
const IMP_LABEL_KEY = "impersonation_label";
/**
 * Id of the impersonated company, so "Back to my account" (and a sign-out while
 * impersonating) can land the super-admin on that company's detail page.
 * Absent for sessions started before this key existed → fallback to the list.
 */
const IMP_COMPANY_KEY = "impersonation_company_id";

/**
 * View state saved in this browser for the signed-in account. It must not carry
 * over to the other identity: a super-admin's saved Transportator filter, for
 * one, would silently empty a partner's booking list (the select is hidden there).
 */
const IDENTITY_SCOPED_KEYS = ["bookings-filter"];

function clearIdentityScopedState(): void {
  IDENTITY_SCOPED_KEYS.forEach((key) => localStorage.removeItem(key));
}

/**
 * Map known backend soft-error codes (data.message.message) to localized
 * messages. Anything not listed — including raw English strings like
 * "Company not found" — falls back to a generic Romanian message rather than
 * being echoed to the user verbatim.
 */
const IMP_ERROR_MESSAGES: Record<string, string> = {
  NO_COMPANY_ADMIN:
    "Această companie nu are un cont de administrator pentru a te loga.",
  "Company not found": "Compania nu a fost găsită.",
};
const IMP_ERROR_FALLBACK = "Nu se poate loga ca partener.";

export function isImpersonating(): boolean {
  return !!localStorage.getItem(IMP_TOKEN_KEY);
}

export function impersonationLabel(): string {
  return localStorage.getItem(IMP_LABEL_KEY) || "";
}

/** Clear impersonation bookkeeping without navigating (used on logout). */
export function clearImpersonationKeys(): void {
  localStorage.removeItem(IMP_TOKEN_KEY);
  localStorage.removeItem(IMP_LABEL_KEY);
  localStorage.removeItem(IMP_COMPANY_KEY);
}

/** Strict positive-integer parse; anything else (null, "", "abc", "1.5", "0") → null. */
export function parseCompanyId(value: unknown): number | null {
  const raw = typeof value === "number" ? String(value) : value;
  if (typeof raw !== "string" || !/^[1-9]\d*$/.test(raw)) return null;
  const id = Number(raw);
  return Number.isSafeInteger(id) ? id : null;
}

/** Where "Back to my account" lands: the impersonated company's detail page, or the list. */
export function impersonationReturnPath(): string {
  const id = parseCompanyId(localStorage.getItem(IMP_COMPANY_KEY));
  return id ? `/companies/${id}` : "/companies";
}

function hardNavigate(hashPath: string): void {
  // Hash-history app: set the route, then force a full reload so the new
  // identity's stores/realtime wiring re-initialise cleanly.
  window.location.hash = hashPath.startsWith("#") ? hashPath : `#${hashPath}`;
  window.location.reload();
}

export async function startImpersonation(companyId: number, fallbackLabel = ""): Promise<boolean> {
  const globalStore = useGlobalStore();
  try {
    const { data } = await ApiService.post("login-as", { company_id: companyId });

    if (data?.error || !data?.user?.access_token) {
      const code = data?.message?.message;
      globalStore.addNotificationError(
        IMP_ERROR_MESSAGES[code] || IMP_ERROR_FALLBACK
      );
      return false;
    }

    const original = JwtService.getToken();
    if (original) {
      localStorage.setItem(IMP_TOKEN_KEY, original);
    }
    localStorage.setItem(
      IMP_LABEL_KEY,
      data.user.company || data.user.name || fallbackLabel || `#${companyId}`
    );
    const returnCompanyId = parseCompanyId(companyId);
    if (returnCompanyId) {
      localStorage.setItem(IMP_COMPANY_KEY, String(returnCompanyId));
    } else {
      localStorage.removeItem(IMP_COMPANY_KEY);
    }
    clearIdentityScopedState();
    webPushIdentityChanging();
    JwtService.saveToken(data.user.access_token);
    hardNavigate("/dashboard");
    return true;
  } catch (e: unknown) {
    const status = (e as { response?: { status?: number } })?.response?.status;
    globalStore.addNotificationError(
      status === 403
        ? "Nu ai dreptul să te loghezi ca partener."
        : "Eroare la logarea ca partener."
    );
    return false;
  }
}

export function stopImpersonation(): void {
  webPushIdentityChanging();
  const original = localStorage.getItem(IMP_TOKEN_KEY);
  if (original) {
    JwtService.saveToken(original);
  }
  // Read before clearing: the company id lives in one of the keys being wiped.
  const returnPath = impersonationReturnPath();
  clearImpersonationKeys();
  clearIdentityScopedState();
  hardNavigate(returnPath);
}
